/**
 * The entry point: build the four singletons, mount the router.
 *
 * Everything below this file takes its dependencies as arguments, which is why
 * this one is excluded from coverage — what is here is the wiring a browser does
 * once and a test never does.
 */

import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { createApiClient } from './api/client.js'
import { authClient } from './auth/client.js'
import { AppProviders, createQueryClient } from './providers.js'
import { createAppRouter } from './router.js'
import './styles/index.css'

const queryClient = createQueryClient()
const api = createApiClient()
const router = createAppRouter({ queryClient, auth: authClient })

const root = document.getElementById('root')
if (root === null) throw new Error('index.html has no #root to mount into.')

createRoot(root).render(
  <StrictMode>
    <AppProviders queryClient={queryClient} api={api} auth={authClient}>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>,
)

/*
 * The boot screen is taken **out of the document** once the app is in it, not just
 * hidden.
 *
 * `index.html`'s `.booting` is a `role="status"` live region, which is what makes a
 * slow first load say something rather than sit blank. `base.css`'s
 * `#root:not(:empty) ~ .booting` stops it being seen at the instant React commits,
 * and that is still what decides the pixels — but a hidden node is a node, and the
 * page now has a live region of its own (`taskNotice.tsx`). Two of them, one
 * carrying "Loading your portal…" for the rest of the session, is a reader asking
 * what is being announced and hearing the boot text.
 *
 * So: the same signal the stylesheet uses — the first child in `#root` — and then
 * the node goes. Nothing is kept in step, because there is nothing left to keep.
 */
const booting = document.querySelector('.booting')
if (booting !== null) {
  if (root.childNodes.length > 0) {
    booting.remove()
  } else {
    const watch = new MutationObserver(() => {
      if (root.childNodes.length === 0) return
      watch.disconnect()
      booting.remove()
    })
    watch.observe(root, { childList: true })
  }
}
